import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { idealistRequireAuthentication } from '#/modules/auth/routing/idealist-require-authentication';
import { orgDashboardManageListingsRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { userDashboardSelectOrgRoute } from '#/modules/userDashboard/routing/userDashboard.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import { createOrgRoute } from '#/routing/routes';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';

import { UserDashboardSelectOrgPageLoadable } from './UserDashboardSelectOrgPageLoadable';

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof userDashboardSelectOrgRoute>) {
  const [{ user }] = await Promise.all([
    apiIsomorphicFetchUserEnvironment({ request, context })(),
    UserDashboardSelectOrgPageLoadable.load(),
  ]);

  if (!user) return idealistRequireAuthentication({ request, context });

  if (user.orgs.length === 0) {
    return idealistRedirect({ request, context, to: createOrgRoute.with({}) });
  } else if (user.orgs.length === 1) {
    return idealistRedirect({
      request,
      context,
      to: orgDashboardManageListingsRoute.with({ orgId: user.orgs[0].id }),
    });
  }

  return { orgs: user.orgs, locale: context.locale };
}

export default function UserDashboardSelectOrgHorizonRoute({
  loaderData: { orgs },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule title={i18n('Select Organization')}>
      <UserDashboardSelectOrgPageLoadable orgs={orgs} />
    </RouteModule>
  );
}
